import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useOptimization } from '../ui/optimization-progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertCircle } from 'lucide-react';
import { Product } from '../../types/product';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { bulkOptimizationService } from '../../services/optimization/BulkOptimizationService';

interface Prompt {
  id: string;
  marketplace: string;
  title: string;
  content: string;
  isDefault: boolean;
  lastUsed?: Date;
  status: 'Active' | 'Inactive';
}

interface BulkOptimizationDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  products: Product[];
  onComplete: () => void;
  onClearSelection?: () => void;
}

const BulkOptimizationDialog: React.FC<BulkOptimizationDialogProps> = ({
  isOpen,
  onOpenChange,
  products,
  onComplete,
  onClearSelection,
}) => {
  const [error, setError] = useState('');
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const hasStartedRef = useRef(false);
  const { showProgress, hideProgress } = useOptimization();

  const clearAllTimers = useCallback(() => {
    setError('');
    hasStartedRef.current = false;
  }, []);

  const handleBulkOptimize = () => {
    const totalProducts = products.length;
    
    // Show initial progress immediately with loading spinner
    showProgress(`Starting optimization of ${totalProducts} products...`, 'loading');

    // Close dialog and clear selection
    onOpenChange(false);
    if (onClearSelection) {
      onClearSelection();
    }

    // Start optimization in the background
    bulkOptimizationService.enqueueBulkOptimization(
      products.map(p => p.id),
      'Shopify',
      selectedPrompt
    ).catch(error => {
      console.error('Bulk optimization failed:', error);
      showProgress('Failed to start bulk optimization 😢', 'success');
      setTimeout(hideProgress, 3000);
    });

    // Listen for individual product completions
    bulkOptimizationService.onProductComplete((productId, total, current) => {
      showProgress(`Optimizing products... (${current} of ${total} complete)`, 'loading');
    });

    // Listen for overall completion
    bulkOptimizationService.onComplete(() => {
      showProgress('All optimizations completed successfully! 🎉', 'success');
      setTimeout(() => {
        hideProgress();
        onComplete();
      }, 3000);
    });
  };

  const handleClose = useCallback(() => {
    clearAllTimers();
    setSelectedPrompt('');
    onOpenChange(false);
  }, [clearAllTimers, onOpenChange]);

  useEffect(() => {
    return clearAllTimers;
  }, [clearAllTimers]);

  const loadPrompts = useCallback(async () => {
    setIsLoadingPrompts(true);
    setError('');
    
    try {
      const marketplacePrompts = await bulkOptimizationService.getPrompts('Shopify');

      if (marketplacePrompts.length === 0) {
        throw new Error('No prompts found for Shopify');
      }

      const activePrompts = marketplacePrompts.map(prompt => ({
        id: prompt.id,
        marketplace: prompt.marketplace,
        title: prompt.title,
        content: prompt.content,
        isDefault: prompt.isDefault,
        lastUsed: prompt.lastUsed,
        status: (prompt.isDefault ? 'Active' : 'Inactive') as 'Active' | 'Inactive'
      }));
      
      setPrompts(activePrompts);
      setSelectedPrompt(activePrompts[0]?.id || '');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to load prompts');
    } finally {
      setIsLoadingPrompts(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadPrompts();
    } else {
      clearAllTimers();
      setError('');
      setSelectedPrompt('');
      hasStartedRef.current = false;
    }
  }, [isOpen, loadPrompts, clearAllTimers]);

  const getSelectedPromptContent = () => {
    const selectedPromptData = prompts.find(p => p.id === selectedPrompt);
    return selectedPromptData?.content || '';
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Bulk Optimize Products</DialogTitle>
          <DialogDescription className="text-base">
            Optimizing {products.length} products
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-medium mb-2">Select Prompt Template</h3>
            {isLoadingPrompts ? (
              <div className="text-sm text-gray-500">Loading prompts...</div>
            ) : (
              <Select
                value={selectedPrompt}
                onValueChange={setSelectedPrompt}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a prompt template" />
                </SelectTrigger>
                <SelectContent>
                  {prompts.map((prompt) => (
                    <SelectItem key={prompt.id} value={prompt.id}>
                      {prompt.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {selectedPrompt && (
            <div>
              <h3 className="text-sm font-medium mb-2">Prompt Preview</h3>
              <div className="bg-gray-50 rounded-md p-3 text-sm text-gray-700 max-h-60 overflow-y-auto">
                {getSelectedPromptContent().split('\n').filter(Boolean).map((line, i) => (
                  <p key={i} className="mb-2">{line}</p>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkOptimize}
            disabled={!selectedPrompt || isLoadingPrompts}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Start Bulk Optimization
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkOptimizationDialog;